import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { translations } from './translations';

const LanguageContext = createContext(null);

const getInitialLang = () => {
  if (typeof window === 'undefined') return 'en';
  const saved = window.localStorage?.getItem('lang');
  if (saved === 'en' || saved === 'es') return saved;
  // Auto-detect from browser language
  const browserLang = window.navigator.language || window.navigator.userLanguage || 'en';
  return browserLang.toLowerCase().startsWith('es') ? 'es' : 'en';
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(getInitialLang);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('lang', lang);
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Resolve a path like "home.heroLine1" against the dictionary
const resolvePath = (dict, path) => {
  if (!dict || !path) return undefined;
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), dict);
};

export const useTranslation = () => {
  const ctx = useContext(LanguageContext);
  const lang = ctx?.lang ?? 'en';
  const setLang = ctx?.setLang ?? (() => {});

  // t() accepts either a dot-path string ("home.heroLine1")
  // or a bilingual object ({en: "...", es: "..."}) and returns the right value.
  const t = useCallback(
    (input) => {
      if (input == null) return '';
      if (typeof input === 'object') {
        return input[lang] ?? input.en ?? '';
      }
      const resolved = resolvePath(translations[lang], input);
      if (resolved != null) return resolved;
      // Fall back to English if the key is missing in the current language
      const fallback = resolvePath(translations.en, input);
      return fallback ?? input;
    },
    [lang]
  );

  return { t, lang, setLang };
};
